/**
 * Selling Plans Widget — OS 2.0 Compatible
 * Handles section rendering (DOM replacement on variant change)
 * Architecture: 2 radio buttons (one-time / subscription) + frequency dropdown
 */

const hiddenClass = 'selling_plan_theme_integration--hidden';

class SellingPlansWidget {
  constructor(container) {
    this.sectionId = container.getAttribute('data-section-id');

    // Use the shopify section wrapper as root — this element survives section rendering
    this.root = document.querySelector(`#shopify-section-${this.sectionId}`);
    if (!this.root) {
      console.warn('[SellingPlans] Section not found:', this.sectionId);
      return;
    }

    this.appendHiddenInputs();
    this.syncState();
    this.bindDelegatedEvents();
    this.observeVariantInput();
    this.observePerformance();
    this.listenToSectionRender();
  }

  /* ─────────────────────────────────────────────
     LIVE DOM QUERIES (always fresh, never stale)
     Every getter re-queries the DOM so we never
     hold references to replaced elements.
  ───────────────────────────────────────────── */
  get container() {
    return this.root.querySelector('.selling_plan_app_container');
  }

  get addToCartForms() {
    return this.root.querySelectorAll('[action*="/cart/add"]');
  }

  get variantIdInput() {
    const forms = this.addToCartForms;
    for (let i = forms.length - 1; i >= 0; i--) {
      const el =
        forms[i].querySelector('input[name="id"]') ||
        forms[i].querySelector('select[name="id"]');
      if (el) return el;
    }
    return null;
  }

  get currentVariantId() {
    return this.variantIdInput?.value || null;
  }

  get visibleSection() {
    if (!this.currentVariantId) return null;
    return this.root.querySelector(
      `section.selling_plan_theme_integration[data-variant-id="${this.currentVariantId}"]`
    );
  }

  get checkedRadio() {
    return this.visibleSection?.querySelector('input[type="radio"]:checked') || null;
  }

  get frequencySelect() {
    return this.visibleSection?.querySelector('.subscription-frequency-select') || null;
  }

  get allHiddenInputs() {
    return this.root.querySelectorAll('.selected-selling-plan-id');
  }

  get originalHiddenInput() {
    return this.root.querySelector('.selected-selling-plan-id');
  }

  /* ── Price elements ── */
  get priceGroup() {
    return this.root.querySelector('.product-price-group');
  }
  get regularPriceEl() {
    return this.root.querySelector('.price__regular');
  }
  get salePriceEl() {
    return this.root.querySelector('.price__sale');
  }
  get salePriceValue() {
    return this.salePriceEl?.querySelector('.price-item--sale');
  }
  get regularPriceValue() {
    return this.salePriceEl?.querySelector('.price-item--regular');
  }

  /* ─────────────────────────────────────────────
     HIDDEN INPUTS — ensure every form has one
  ───────────────────────────────────────────── */
  appendHiddenInputs() {
    const original = this.originalHiddenInput;
    if (!original) return;

    this.addToCartForms.forEach((form) => {
      if (form.querySelector('.selected-selling-plan-id')) return;
      form.appendChild(original.cloneNode());
    });
  }

  /* ─────────────────────────────────────────────
     SECTION VISIBILITY
  ───────────────────────────────────────────── */
  updateSectionVisibility() {
    const variantId = this.currentVariantId;
    if (!variantId) return;

    this.root
      .querySelectorAll('section.selling_plan_theme_integration')
      .forEach((section) => {
        if (section.getAttribute('data-variant-id') === variantId) {
          section.classList.remove(hiddenClass);
        } else {
          section.classList.add(hiddenClass);
        }
      });
  }

  selectDefaultOption() {
    const section = this.visibleSection;
    if (!section) return;

    const radios = section.querySelectorAll('input[type="radio"]');
    if (radios.length === 0) return;

    const hasChecked = [...radios].some((r) => r.checked);
    if (!hasChecked) {
      radios[0].checked = true;
    }
  }

  /* ─────────────────────────────────────────────
     CORE STATE SYNC
  ───────────────────────────────────────────── */
  syncState() {
    this.updateSectionVisibility();
    this.selectDefaultOption();

    const radio = this.checkedRadio;
    if (!radio) return;

    const isSubscription = radio.dataset.radioType === 'selling_plan';
    const freqSelector = this.visibleSection?.querySelector('.subscription-frequency-selector');
    const select = this.frequencySelect;

    // Toggle dropdown (use CSS class for animated transition)
    if (freqSelector) {
      if (isSubscription) {
        freqSelector.classList.add('bz-open');
      } else {
        freqSelector.classList.remove('bz-open');
      }
    }

    let sellingPlanId = '';
    let displayPrice = '';
    let displayCompareAtPrice = '';

    if (isSubscription && select) {
      const selectedOption = select.options[select.selectedIndex];
      sellingPlanId = select.value;
      displayPrice = selectedOption?.dataset.price || radio.dataset.variantPrice;
      displayCompareAtPrice = selectedOption?.dataset.compareAtPrice || radio.dataset.variantCompareAtPrice;

      // Keep radio in sync with dropdown
      radio.dataset.sellingPlanId = sellingPlanId;
      radio.dataset.variantPrice = displayPrice;
      radio.dataset.variantCompareAtPrice = displayCompareAtPrice;
    } else {
      sellingPlanId = '';
      displayPrice = radio.dataset.variantPrice;
      displayCompareAtPrice = radio.dataset.variantCompareAtPrice;
    }

    // Update all hidden inputs
    this.allHiddenInputs.forEach((input) => {
      input.value = sellingPlanId;
    });

    this.updatePrice(displayPrice, displayCompareAtPrice);
  }

  /* ─────────────────────────────────────────────
     PRICE DISPLAY
  ───────────────────────────────────────────── */
  updatePrice(price, compareAtPrice) {
    if (!this.regularPriceEl || !this.salePriceEl) return;

    const hasDiscount =
      compareAtPrice && compareAtPrice !== price && compareAtPrice !== '';

    if (hasDiscount) {
      this.regularPriceEl.style.display = 'none';
      this.salePriceEl.style.display = 'block';
      if (this.priceGroup) this.priceGroup.classList.add('price--on-sale');
      if (this.regularPriceValue) this.regularPriceValue.innerHTML = compareAtPrice;
      if (this.salePriceValue) this.salePriceValue.innerHTML = price;
    } else {
      this.regularPriceEl.style.display = 'block';
      this.salePriceEl.style.display = 'none';
      if (this.priceGroup) this.priceGroup.classList.remove('price--on-sale');

      const regularDisplay = this.regularPriceEl.querySelector('.price-item--regular');
      if (regularDisplay && price) {
        regularDisplay.innerHTML = price;
      }
    }
  }

  /* ─────────────────────────────────────────────
     EVENT DELEGATION
     All events are bound on this.root which is
     the #shopify-section-xxx wrapper that persists
     even when inner content is replaced.
  ───────────────────────────────────────────── */
  bindDelegatedEvents() {
    this.root.addEventListener('change', (e) => {
      const target = e.target;

      // Radio button change (purchase option)
      if (target.matches('input[type="radio"][name^="purchaseOption_"]')) {
        this.syncState();
        return;
      }

      // Frequency dropdown change
      if (target.matches('.subscription-frequency-select')) {
        this.onFrequencyChange(target);
        return;
      }

      // Any change inside a cart form (variant selector, etc.)
      if (target.closest('[action*="/cart/add"]')) {
        requestAnimationFrame(() => this.syncState());
        return;
      }
    });
  }

  onFrequencyChange(select) {
    const section = this.visibleSection;
    if (!section) return;

    const subscriptionRadio = section.querySelector(
      'input[data-radio-type="selling_plan"]'
    );
    if (!subscriptionRadio) return;

    const selectedOption = select.options[select.selectedIndex];
    subscriptionRadio.dataset.sellingPlanId = select.value;
    subscriptionRadio.dataset.variantPrice = selectedOption.dataset.price;
    subscriptionRadio.dataset.variantCompareAtPrice =
      selectedOption.dataset.compareAtPrice;

    if (subscriptionRadio.checked) {
      this.syncState();
    }

    // Custom event for external listeners (e.g. FastBundle)
    select.dispatchEvent(
      new CustomEvent('selling-plan:changed', {
        detail: {
          sellingPlanId: select.value,
          price: selectedOption.dataset.price,
          compareAtPrice: selectedOption.dataset.compareAtPrice,
        },
        bubbles: true,
      })
    );
  }

  /* ─────────────────────────────────────────────
     VARIANT INPUT OBSERVER
     Watches input[name="id"] for attribute changes
     (themes set value via setAttribute)
  ───────────────────────────────────────────── */
  observeVariantInput() {
    this._setupVariantObserver();
  }

  _setupVariantObserver() {
    const input = this.variantIdInput;
    if (!input || input.tagName !== 'INPUT') return;

    if (this._variantObserver) {
      this._variantObserver.disconnect();
    }

    this._lastVariantValue = input.value;

    this._variantObserver = new MutationObserver(() => {
      const newValue = input.value;
      // Only react if value actually changed
      if (newValue !== this._lastVariantValue) {
        this._lastVariantValue = newValue;
        this.syncState();
      }
    });

    this._variantObserver.observe(input, { attributes: true });
  }

  /* ─────────────────────────────────────────────
     SECTION RENDERING LISTENER
     OS 2.0 themes replace section HTML on variant
     change. We detect this and reinitialize.
  ───────────────────────────────────────────── */
  listenToSectionRender() {
    // Standard Shopify event (theme editor + some themes)
    document.addEventListener('shopify:section:load', (e) => {
      if (e.detail?.sectionId === this.sectionId) {
        this.reinitialize();
      }
    });

    // Some themes dispatch custom events
    document.addEventListener('variant:changed', () => {
      requestAnimationFrame(() => this.syncState());
    });

    // Fallback: watch root for child DOM replacement
    this._sectionObserver = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type !== 'childList' || mutation.addedNodes.length === 0) continue;

        // If new nodes contain our container, the section was re-rendered
        for (const node of mutation.addedNodes) {
          if (node.nodeType !== 1) continue;
          if (
            node.classList?.contains('selling_plan_app_container') ||
            node.querySelector?.('.selling_plan_app_container')
          ) {
            this.reinitialize();
            return;
          }
        }
      }
    });

    this._sectionObserver.observe(this.root, {
      childList: true,
      subtree: true,
    });
  }

  /**
   * Re-setup after DOM replacement
   */
  reinitialize() {
    this.appendHiddenInputs();
    this._setupVariantObserver();
    this.syncState();
  }

  /* ─────────────────────────────────────────────
     PERFORMANCE OBSERVER
     Catches variant fetch requests and re-syncs
  ───────────────────────────────────────────── */
  observePerformance() {
    try {
      const perfObserver = new PerformanceObserver((list) => {
        list.getEntries().forEach((entry) => {
          if (entry.initiatorType !== 'fetch') return;
          try {
            const url = new URL(entry.name);
            if (
              url.search.includes('variant') ||
              url.search.includes('variants')
            ) {
              // Delay to let theme finish DOM updates
              setTimeout(() => this.syncState(), 150);
            }
          } catch (e) {
            // Invalid URL
          }
        });
      });
      perfObserver.observe({ entryTypes: ['resource'] });
    } catch (e) {
      // Not supported
    }
  }
}

/* ─────────────────────────────────────────────
   INIT
───────────────────────────────────────────── */
document.querySelectorAll('.selling_plan_app_container').forEach((container) => {
  new SellingPlansWidget(container);
});